import { STORE_ORIGIN } from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ProductsPage.css';

const Products = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  const navigate = useNavigate();

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await axios.get(`${STORE_ORIGIN}/api/api.php`);
        
        if (Array.isArray(response.data) && response.data.length > 0) {
          setData(response.data);
        } else {
          setError('No products available');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message || 'An error occurred while fetching products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Extract all categories for filter dropdown
  const allCategories = data.flatMap(sport => 
    sport.categories.map(category => ({
      id: category.id,
      name: category.name,
      slug: category.slug,
      sport: sport.name,
      sportSlug: sport.slug
    }))
  );

  // Extract all subcategories for filter dropdown
  const allSubcategories = data.flatMap(sport =>
    sport.categories.flatMap(category =>
      category.subcategories.map(subcategory => ({
        id: subcategory.id,
        name: subcategory.name,
        slug: subcategory.slug,
        categoryName: category.name,
        categorySlug: category.slug,
        sport: sport.name,
        sportSlug: sport.slug
      }))
    )
  );

  // Get filtered products based on selections
  const getFilteredProducts = () => {
    let filtered = [];

    data.forEach(sport => {
      sport.categories.forEach(category => {
        category.subcategories.forEach(subcategory => {
          // Apply category filter
          if (selectedCategory !== 'all' && category.id !== parseInt(selectedCategory)) {
            return;
          }
          
          // Apply subcategory filter
          if (selectedSubcategory !== 'all' && subcategory.id !== parseInt(selectedSubcategory)) {
            return;
          }

          // Apply search filter
          const productsInSubcategory = subcategory.products.filter(product => {
            const matchesSearch = searchTerm === '' || 
              product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase()));
            return matchesSearch;
          });

          if (productsInSubcategory.length > 0) {
            filtered.push({
              sport: sport.name,
              sportSlug: sport.slug,
              category: category.name,
              categorySlug: category.slug,
              subcategory: subcategory.name,
              subcategorySlug: subcategory.slug,
              products: productsInSubcategory.map(product => ({
                ...product,
                sportSlug: sport.slug,
                categorySlug: category.slug,
                subcategorySlug: subcategory.slug
              }))
            });
          }
        });
      });
    });

    return filtered;
  };

  const filteredGroups = getFilteredProducts();

  const handleViewProduct = (product) => {
    // Using slug for product detail page URL
    navigate(`/product/${product.slug}`);
  };

  // Reset subcategory when category changes
  useEffect(() => {
    setSelectedSubcategory('all');
  }, [selectedCategory]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading products...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">Error: {error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="retry-button"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="products-page">
      <h1 className="page-title">FightGear Products</h1>
      
      {/* Search and Filter Controls */}
      <div className="products-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        {/* Category Filter */}
        <div className="filter-container">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Categories</option>
            {allCategories.map(category => (
              <option key={category.id} value={category.id}>
                {category.sport} - {category.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Subcategory Filter */}
        <div className="filter-container">
          <select
            value={selectedSubcategory}
            onChange={(e) => setSelectedSubcategory(e.target.value)}
            className="filter-select"
            disabled={selectedCategory === 'all'}
          >
            <option value="all">All Subcategories</option>
            {selectedCategory !== 'all' && allSubcategories
              .filter(sub => 
                allCategories.find(cat => 
                  cat.id === parseInt(selectedCategory) && 
                  cat.name === sub.categoryName
                )
              )
              .map(subcategory => (
                <option key={subcategory.id} value={subcategory.id}>
                  {subcategory.name}
                </option>
              ))}
          </select>
        </div>
      </div>
      
      {/* Display products grouped by subcategory */}
      {filteredGroups.length === 0 ? (
        <div className="no-products">
          <p>No products found matching your criteria.</p>
        </div>
      ) : (
        <div className="subcategories-container">
          {filteredGroups.map((group, index) => (
            <div key={`${group.categorySlug}-${group.subcategorySlug}-${index}`} className="subcategory-section">
              <div className="subcategory-header">
                <span className="sport-badge">{group.sport}</span>
                <span className="category-badge">{group.category}</span>
                <h2 className="subcategory-title">{group.subcategory}</h2>
              </div>
              
              <div className="products-grid">
                {group.products.map((product) => (
                  <div key={product.slug} className="product-card">
                    <div 
                      className="product-image-container"
                      onClick={() => handleViewProduct(product)}
                      style={{ cursor: 'pointer' }}
                    >
                      <img 
                        src={product.main_image.includes('uploads/products/') 
                          ? `${STORE_ORIGIN}/admin/${product.main_image}`
                          : product.main_image.includes('../uploads/products/')
                            ? `${STORE_ORIGIN}/admin/${product.main_image.replace('../', '')}`
                            : `${STORE_ORIGIN}/admin/uploads/products/${product.main_image}`
                        }
                        alt={product.name}
                        className="product-image"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/300x300?text=No+Image';
                        }}
                      />
                    </div>
                    <div className="product-details">
                      <h3 className="product-name">{product.name}</h3>
                      
                      {product.description && (
                        <div className="product-description">
                          {product.description.length > 100 
                            ? `${product.description.substring(0, 100)}...` 
                            : product.description}
                        </div>
                      )}
                      
                      <div className="product-meta">
                        <div className="product-price">QAR {parseFloat(product.base_price).toFixed(2)}</div>
                        {product.is_featured && (
                          <span className="featured-badge">Featured</span>
                        )}
                      </div>
                      
                      <div className="product-actions">
                        <button 
                          className="view-product-button"
                          onClick={() => handleViewProduct(product)}
                        >
                          View Product
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Products;